// src/features/public-booking/PublicBookingPage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CheckCircle, ChevronRight, ChevronLeft, Calendar, User,
  Clock, AlertCircle, Stethoscope, Phone, Building2, Copy,
  Sun, Moon, Search, MapPin, Star,
} from 'lucide-react';
import { format, parseISO, addDays, differenceInCalendarDays } from 'date-fns';
import { doctorService, departmentService, appointmentService, slotService } from '../../api/services';
import {
  AvailableSlotDto, CreateAppointmentRequest, FlexibleSlotDto,
  DoctorListDto, DoctorDetailDto, DepartmentDto,
} from '../../api/types';
import toast from 'react-hot-toast';
import clsx from 'clsx';

/* ────────────────────────────── Types ────────────────────────────── */

type BookingMode = 'department' | 'doctor' | 'session';

interface PublicBookingState {
  departmentId?: number;
  doctorId?: number;
  slotDate?: string;
  sessionType?: string;
  patientName: string;
  patientPhone: string;
  patientGender: string;
  patientEmail: string;
  patientNotes: string;
  isOnCall?: boolean;
}

const INITIAL_FORM: PublicBookingState = {
  patientName: '',
  patientPhone: '',
  patientGender: '',
  patientEmail: '',
  patientNotes: '',
};

/* ────────────────── Step definitions per mode ────────────────── */

const MODE_STEPS: Record<BookingMode, { label: string }[]> = {
  department: [
    { label: 'Speciality' },
    { label: 'Slot' },
    { label: 'Details' },
  ],
  doctor: [
    { label: 'Doctor' },
    { label: 'Slot' },
    { label: 'Details' },
  ],
  session: [
    { label: 'Session' },
    { label: 'Slot' },
    { label: 'Details' },
  ],
};

/* ────────────────────────────── Component ────────────────────────── */

export function PublicBookingPage() {
  const [searchParams] = useSearchParams();
  const doctorSlug = searchParams.get('doctorSlug');

  const [mode, setMode] = useState<BookingMode | null>(doctorSlug ? 'doctor' : null);
  const [step, setStep] = useState(doctorSlug ? 2 : 1);
  const [booking, setBooking] = useState<PublicBookingState>(INITIAL_FORM);
  const [confirmedAppointment, setConfirmedAppointment] = useState<any>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorListDto | DoctorDetailDto | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentDto | null>(null);
  const [sessionFilter, setSessionFilter] = useState<'Morning' | 'Evening'>('Morning');
  const [doctorDeptFilter, setDoctorDeptFilter] = useState<number | undefined>(undefined);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [slotToDate, setSlotToDate] = useState(() => format(addDays(new Date(), 7), 'yyyy-MM-dd'));

  // daysAhead derived from slotToDate (min 7)
  const slotDaysAhead = Math.max(7, differenceInCalendarDays(parseISO(slotToDate), new Date()) + 1);
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const minToDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  const isDeepLink = !!doctorSlug;

  /* ── Deep-link: resolve doctor by slug ─────────────────────────── */

  const {
    data: preselectedDoctor,
    isLoading: preselecting,
    isError: preselectionFailed,
  } = useQuery({
    queryKey: ['doctor', doctorSlug],
    queryFn: () => doctorService.getBySlug(doctorSlug!),
    enabled: !!doctorSlug,
  });

  useEffect(() => {
    if (preselectedDoctor) {
      setSelectedDoctor(preselectedDoctor);
      setBooking((b) => ({
        ...b,
        doctorId: preselectedDoctor.id,
        departmentId: preselectedDoctor.departmentId,
        isOnCall: preselectedDoctor.isOnCall,
      }));
      setMode('doctor');
      setStep(2);
    }
  }, [preselectedDoctor]);

  /* ── Data queries ───────────────────────────────────────────────── */

  const { data: departments = [], isLoading: deptsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: departmentService.getAll,
    enabled: mode !== null,
  });

  // Mode: doctor — step 1 doctor list (all, optional dept filter)
  const { data: doctors = [], isLoading: doctorsLoading } = useQuery({
    queryKey: ['doctors', { departmentId: doctorDeptFilter }],
    queryFn: () => doctorService.getAll({ departmentId: doctorDeptFilter, acceptingBookings: true }),
    enabled: mode === 'doctor' && step === 1,
  });

  // Mode: doctor — step 2 per-doctor slots
  const { data: doctorSlots = [], isLoading: doctorSlotsLoading } = useQuery({
    queryKey: ['slots', booking.doctorId, slotToDate],
    queryFn: () => doctorService.getAvailableSlots(booking.doctorId!, todayStr, slotToDate),
    enabled: mode === 'doctor' && !!booking.doctorId && step === 2,
  });

  // Mode: department — step 2 flexible slots
  const { data: deptSlots = [], isLoading: deptSlotsLoading } = useQuery({
    queryKey: ['flex-slots-dept', booking.departmentId, slotToDate],
    queryFn: () => slotService.getByDepartment(booking.departmentId!, slotDaysAhead),
    enabled: mode === 'department' && !!booking.departmentId && step === 2,
    staleTime: 60_000,
  });

  // Mode: session — step 2 flexible slots
  const { data: sessionSlots = [], isLoading: sessionSlotsLoading } = useQuery({
    queryKey: ['flex-slots-session', sessionFilter, booking.departmentId, slotToDate],
    queryFn: () => slotService.getBySession(sessionFilter, booking.departmentId, slotDaysAhead),
    enabled: mode === 'session' && step === 2,
    staleTime: 60_000,
  });

  /* ── Submit mutation ────────────────────────────────────────────── */

  const { mutate: submitBooking, isPending: submitting } = useMutation({
    mutationFn: (req: CreateAppointmentRequest) => appointmentService.create(req),
    onSuccess: (data) => {
      setConfirmedAppointment(data);
      goToConfirmation();
      toast.success('Appointment booked! Awaiting confirmation.');
    },
    onError: (err: any) => {
      if (err.statusCode === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else {
        toast.error(err.message || 'Booking failed. Please try again.');
      }
    },
  });

  /* ── Navigation helpers ─────────────────────────────────────────── */

  // On-call doctors in doctor mode skip the slot step
  const effectiveSteps = mode
    ? (mode === 'doctor' && booking.isOnCall
      ? MODE_STEPS.doctor.filter(s => s.label !== 'Slot')
      : MODE_STEPS[mode])
    : [];
  const detailsStep = effectiveSteps.length;
  const confirmationStep = detailsStep + 1;

  function goToConfirmation() {
    setStep(confirmationStep);
  }

  /* ── Handlers ───────────────────────────────────────────────────── */

  const selectMode = (m: BookingMode) => {
    setMode(m);
    setStep(1);
    setBooking({ ...INITIAL_FORM });
    setSelectedDoctor(null);
    setSelectedDepartment(null);
    setDoctorDeptFilter(undefined);
    setDoctorSearch('');
  };

  const selectDepartment = (d: DepartmentDto) => {
    setSelectedDepartment(d);
    setBooking((b) => ({ ...b, departmentId: d.id }));
    setStep(2);
  };

  const selectDoctor = (d: DoctorListDto) => {
    setSelectedDoctor(d);
    setBooking((b) => ({ ...b, doctorId: d.id, departmentId: d.departmentId, isOnCall: d.isOnCall }));
    setStep(2);
  };

  const selectDoctorSlot = (slot: AvailableSlotDto) => {
    setBooking((b) => ({ ...b, slotDate: slot.slotDate, sessionType: slot.sessionType }));
    setStep(detailsStep);
  };

  const selectFlexibleSlot = (slot: FlexibleSlotDto) => {
    setSelectedDoctor({ name: slot.doctorName, departmentName: slot.departmentName } as DoctorListDto);
    setBooking((b) => ({
      ...b,
      doctorId: slot.doctorId,
      departmentId: slot.departmentId,
      slotDate: slot.slotDate,
      sessionType: slot.sessionType,
      isOnCall: slot.isOnCall,
    }));
    setStep(detailsStep);
  };

  const proceedSessionStep = () => {
    setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking.doctorId) return;
    // Regular doctors require date+session; on-call doctors don't
    if (!booking.isOnCall && (!booking.slotDate || !booking.sessionType)) return;

    submitBooking({
      doctorId: booking.doctorId,
      patientName: booking.patientName,
      patientPhone: booking.patientPhone,
      patientGender: booking.patientGender || undefined,
      patientEmail: booking.patientEmail || undefined,
      patientNotes: booking.patientNotes || undefined,
      bookingSource: 'Website',
      // Only include date/session for non-on-call bookings
      ...(booking.isOnCall ? {} : {
        appointmentDate: booking.slotDate,
        sessionType: booking.sessionType,
      }),
    });
  };

  const resetWizard = () => {
    if (isDeepLink && preselectedDoctor) {
      setMode('doctor');
      setStep(2);
      setBooking({ ...INITIAL_FORM, doctorId: preselectedDoctor.id, departmentId: preselectedDoctor.departmentId, isOnCall: preselectedDoctor.isOnCall });
      setSelectedDoctor(preselectedDoctor);
    } else {
      setMode(null);
      setStep(1);
      setBooking({ ...INITIAL_FORM });
      setSelectedDoctor(null);
      setSelectedDepartment(null);
    }
    setConfirmedAppointment(null);
  };

  /* ── Slot grouping helpers ──────────────────────────────────────── */

  const doctorSlotsByDate = doctorSlots.reduce<Record<string, AvailableSlotDto[]>>((acc, slot) => {
    (acc[slot.slotDate] ??= []).push(slot);
    return acc;
  }, {});

  const flexSlotsByDate = (mode === 'department' ? deptSlots : sessionSlots)
    .reduce<Record<string, FlexibleSlotDto[]>>((acc, slot) => {
      (acc[slot.slotDate] ??= []).push(slot);
      return acc;
    }, {});

  // Mode: session — group by doctor for display
  const sessionSlotsByDoctor = sessionSlots.reduce<Map<number, FlexibleSlotDto>>((map, slot) => {
    if (!map.has(slot.doctorId)) map.set(slot.doctorId, slot);
    return map;
  }, new Map());

  /* ── Step indicator ─────────────────────────────────────────────── */

  const visibleSteps = isDeepLink
    ? [
        { label: 'Pick a Slot', display: 1 },
        { label: 'Your Details', display: 2 },
      ]
    : mode
    ? effectiveSteps.map((s, i) => ({ label: s.label, display: i + 1 }))
    : [];

  const showSteps = mode !== null && step <= detailsStep;

  /* ── Loading state for deep-link resolution ────────────────────── */

  if (doctorSlug && preselecting) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center space-y-3">
          <div className="skeleton h-8 w-48 mx-auto rounded-lg" />
          <div className="skeleton h-4 w-64 mx-auto rounded" />
          <div className="skeleton h-32 w-full max-w-md mx-auto rounded-xl mt-6" />
        </div>
      </div>
    );
  }

  /* ── Error state for invalid slug ──────────────────────────────── */

  if (doctorSlug && preselectionFailed) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Doctor Not Found</h2>
        <p className="text-sm text-slate-500 mb-6">
          We couldn't find a doctor matching "{doctorSlug}". They may no longer be available for online booking.
        </p>
        <a
          href={window.location.pathname}
          onClick={(e) => { e.preventDefault(); window.location.search = ''; }}
          className="btn btn-primary"
        >
          Browse All Departments
        </a>
      </div>
    );
  }

  /* ── Render ─────────────────────────────────────────────────────── */

  return (
    <div className="max-w-4xl mx-auto px-6 py-6">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900">Book an Appointment</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isDeepLink
            ? `Schedule your visit with ${selectedDoctor?.name || 'the doctor'}`
            : mode
            ? 'Follow the steps below to schedule your visit'
            : 'Choose how you\'d like to find your appointment'}
        </p>
      </div>

      {/* Step indicator */}
      {showSteps && (
        <div className="flex items-center gap-0 mb-8">
          {visibleSteps.map((s, idx) => {
            const stepNum = isDeepLink ? (idx === 0 ? 3 : 4) : idx + 1;
            return (
              <div key={idx} className="flex items-center">
                <div
                  className={clsx(
                    'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200',
                    step === stepNum
                      ? 'bg-brand-600 text-white shadow-sm'
                      : step > stepNum
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-400'
                  )}
                >
                  {step > stepNum ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <span className="w-4 h-4 flex items-center justify-center text-[11px] font-bold">
                      {s.display}
                    </span>
                  )}
                  {s.label}
                </div>
                {idx < visibleSteps.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-slate-300 mx-1" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════ MODE SELECTOR ══════════════════════ */}
      {mode === null && (
        <div className="animate-slide-in">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">How would you like to book?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={() => selectMode('department')}
              className="card p-5 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5 text-brand-700" />
              </div>
              <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                By Speciality
              </div>
              <p className="text-xs text-slate-500 mt-1">Browse available slots by department</p>
            </button>

            <button
              onClick={() => selectMode('doctor')}
              className="card p-5 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                <Stethoscope className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                By Doctor
              </div>
              <p className="text-xs text-slate-500 mt-1">Choose a specific doctor first</p>
            </button>

            <button
              onClick={() => selectMode('session')}
              className="card p-5 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-amber-700" />
              </div>
              <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                By Time
              </div>
              <p className="text-xs text-slate-500 mt-1">Find doctors available in morning or evening</p>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ MODE: DEPARTMENT ══════════════ */}

      {/* Department Mode — Step 1: Select Speciality */}
      {mode === 'department' && step === 1 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setMode(null)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-brand-600" />
              Select a Speciality
            </h2>
          </div>

          {deptsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card p-4 space-y-3">
                  <div className="skeleton h-5 w-2/3" />
                </div>
              ))}
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No departments available at the moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {departments.map((d) => (
                <button
                  key={d.id}
                  onClick={() => selectDepartment(d)}
                  className="card p-4 text-left transition-all duration-200 group hover:shadow-card-hover hover:border-brand-200 cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                      {d.name}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 transition-colors flex-shrink-0 mt-0.5" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Department Mode — Step 2: Browse Flexible Slots */}
      {mode === 'department' && step === 2 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              Available Slots
            </h2>
          </div>

          {selectedDepartment && (
            <div className="flex items-center gap-2 p-2.5 bg-brand-50 rounded-xl border border-brand-100 mb-5 text-xs text-brand-700 font-medium">
              <Building2 className="w-3.5 h-3.5" />
              {selectedDepartment.name}
            </div>
          )}

          {/* Date range filter */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-600 whitespace-nowrap">Show slots up to:</span>
            <input
              type="date"
              className="input py-1 text-xs flex-1 max-w-[160px]"
              min={minToDate}
              value={slotToDate}
              onChange={(e) => setSlotToDate(e.target.value)}
            />
          </div>

          {deptSlotsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : deptSlots.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No available slots in the next 7 days</p>
              <button onClick={() => setStep(1)} className="btn btn-secondary btn-sm mt-3">
                Choose another speciality
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {Object.entries(flexSlotsByDate).map(([date, daySlots]) => (
                <div key={date}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                    {(daySlots as FlexibleSlotDto[]).every(s => s.isOnCall)
                      ? 'On Call — Available on Request'
                      : format(parseISO(date), 'EEEE, MMMM d')}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(daySlots as FlexibleSlotDto[]).map((slot, i) => (
                      <button
                        key={i}
                        onClick={() => selectFlexibleSlot(slot)}
                        className="card p-3 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-150 group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full flex-shrink-0 relative overflow-hidden border border-slate-100">
                              {slot.doctorPhotoUrl && (
                                <img src={slot.doctorPhotoUrl} alt={slot.doctorName} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              )}
                              <div className="absolute inset-0 bg-brand-50 flex items-center justify-center">
                                <User className="w-4 h-4 text-brand-500" />
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-slate-900 group-hover:text-brand-700 transition-colors">
                                {slot.doctorTitle && `${slot.doctorTitle} `}{slot.doctorName}
                              </div>
                              <div className="text-xs text-slate-500">{slot.doctorQualifications}</div>
                            </div>
                          </div>
                          {slot.isOnCall && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0">
                              On-Call
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs font-medium mt-2">
                          {slot.isOnCall ? (
                            <span className="text-amber-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              On Call — reception will confirm date &amp; time
                            </span>
                          ) : (
                            <span className="text-brand-700 flex items-center gap-1.5">
                              <Clock className="w-3 h-3" />
                              {slot.sessionType} · {slot.startTime} – {slot.endTime}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex items-center gap-1">
                          <div className={clsx(
                            'h-1 rounded-full flex-1 transition-all',
                            slot.remainingSlots > 5 ? 'bg-emerald-400' :
                            slot.remainingSlots > 2 ? 'bg-amber-400' : 'bg-red-400'
                          )} style={{ width: `${(slot.remainingSlots / slot.maxPatients) * 100}%` }} />
                          <div className="h-1 bg-slate-100 rounded-full flex-1" />
                          <span className={clsx(
                            'text-[10px] font-semibold ml-1',
                            slot.remainingSlots > 5 ? 'text-emerald-600' :
                            slot.remainingSlots > 2 ? 'text-amber-600' : 'text-red-600'
                          )}>
                            {slot.remainingSlots} left
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ MODE: DOCTOR ══════════════ */}

      {/* Doctor Mode — Step 1: Select Speciality */}
      {/* Doctor Mode — Step 1: Choose Doctor */}
      {mode === 'doctor' && step === 1 && (
        <div className="animate-slide-in">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {!isDeepLink && (
              <button onClick={() => setMode(null)} className="btn btn-ghost btn-sm">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-brand-600" />
              Choose a Doctor
            </h2>
          </div>

          {/* Search + filter bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or qualification…"
                value={doctorSearch}
                onChange={(e) => setDoctorSearch(e.target.value)}
                className="input pl-9 w-full"
              />
            </div>
            <select
              value={doctorDeptFilter ?? ''}
              onChange={(e) => setDoctorDeptFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="input sm:w-56"
            >
              <option value="">All Specialities</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          {/* Doctor cards */}
          {doctorsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card p-5 space-y-3">
                  <div className="flex gap-3">
                    <div className="skeleton w-16 h-16 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="skeleton h-4 w-3/4" />
                      <div className="skeleton h-3 w-1/2" />
                      <div className="skeleton h-3 w-1/3" />
                    </div>
                  </div>
                  <div className="skeleton h-3 w-full" />
                </div>
              ))}
            </div>
          ) : (() => {
            const filtered = doctors.filter((d) =>
              !doctorSearch ||
              d.name.toLowerCase().includes(doctorSearch.toLowerCase()) ||
              d.qualifications?.toLowerCase().includes(doctorSearch.toLowerCase())
            );
            return filtered.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-slate-500">No doctors found</p>
                {(doctorDeptFilter || doctorSearch) && (
                  <button onClick={() => { setDoctorDeptFilter(undefined); setDoctorSearch(''); }} className="btn btn-secondary btn-sm mt-3">
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filtered.map((d) => {
                  const initials = d.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
                  return (
                    <div key={d.id} className="card p-5 hover:shadow-card-hover transition-all duration-200 group animate-fade-up">
                      <div className="flex gap-4">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {d.photoUrl ? (
                            <img
                              src={d.photoUrl}
                              alt={d.name}
                              className="w-16 h-16 rounded-full object-cover ring-2 ring-slate-100"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement!.querySelector('.avatar-fallback')!.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={clsx(
                            'w-16 h-16 rounded-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center text-brand-700 font-bold text-lg avatar-fallback',
                            d.photoUrl && 'hidden'
                          )}>
                            {initials}
                          </div>
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-slate-900 leading-tight group-hover:text-brand-700 transition-colors">
                                {d.title ? `${d.title} ` : ''}{d.name}
                              </h3>
                              <p className="text-xs text-slate-500 mt-0.5">{d.qualifications}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <span className={clsx(
                                'text-xs px-2 py-0.5 rounded-full font-medium',
                                d.isAcceptingBookings ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                              )}>
                                {d.isAcceptingBookings ? '● Available' : '○ Unavailable'}
                              </span>
                              {d.isOnCall && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                                  Subject to Availability
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <MapPin className="w-3 h-3" />
                              {d.departmentName}
                            </span>
                            {d.experience && (
                              <span className="flex items-center gap-1 text-xs text-slate-500">
                                <Star className="w-3 h-3" />
                                {d.experience}
                              </span>
                            )}
                          </div>
                          {d.shortBio && (
                            <p className="text-xs text-slate-500 mt-2 line-clamp-2 leading-relaxed">{d.shortBio}</p>
                          )}
                          {!d.isOnCall && d.schedules.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {d.schedules.slice(0, 4).map((s, i) => (
                                <span key={i} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-100">
                                  <Clock className="w-2.5 h-2.5" />
                                  {s.dayName.slice(0, 3)} · {s.sessionType}
                                </span>
                              ))}
                              {d.schedules.length > 4 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                                  +{d.schedules.length - 4} more
                                </span>
                              )}
                            </div>
                          )}
                          {d.isOnCall && (
                            <p className="text-xs text-amber-600 mt-3">On-call doctor — available on a case-by-case basis</p>
                          )}
                        </div>
                      </div>
                      {/* Book button */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                        {d.languages && (
                          <span className="text-xs text-slate-400">🗣️ {d.languages}</span>
                        )}
                        <button
                          onClick={() => selectDoctor(d)}
                          disabled={!d.isAcceptingBookings}
                          className="btn btn-primary btn-sm ml-auto"
                        >
                          Book Appointment
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Doctor Mode — Step 2: Pick a Slot (skipped for on-call doctors) */}
      {mode === 'doctor' && step === 2 && !booking.isOnCall && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            {!isDeepLink && (
              <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              Pick a Slot
            </h2>
          </div>

          {selectedDoctor && (
            <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-xl border border-brand-100 mb-5">
              <div className="w-10 h-10 rounded-full flex-shrink-0 relative overflow-hidden border border-brand-100">
                {'photoUrl' in selectedDoctor && selectedDoctor.photoUrl && (
                  <img src={selectedDoctor.photoUrl} alt={selectedDoctor.name} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <div className="absolute inset-0 bg-brand-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-brand-600" />
                </div>
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">
                  {'title' in selectedDoctor && selectedDoctor.title && `${selectedDoctor.title} `}{selectedDoctor.name}
                </div>
                <div className="text-xs text-slate-500">{selectedDoctor.departmentName}</div>
              </div>
              {booking.isOnCall && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium ml-auto">
                  On-Call
                </span>
              )}
            </div>
          )}

          {booking.isOnCall && (
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mb-4 text-xs text-amber-700">
              Slot timings are approximate for on-call doctors. Reception will call to confirm your appointment.
            </div>
          )}

          {/* Date range filter */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-600 whitespace-nowrap">Show slots up to:</span>
            <input
              type="date"
              className="input py-1 text-xs flex-1 max-w-[160px]"
              min={minToDate}
              value={slotToDate}
              onChange={(e) => setSlotToDate(e.target.value)}
            />
          </div>

          {doctorSlotsLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : doctorSlots.length === 0 && !booking.isOnCall ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No available slots in the next 7 days</p>
              {!isDeepLink && (
                <button onClick={() => setStep(2)} className="btn btn-secondary btn-sm mt-3">
                  Choose another doctor
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(doctorSlotsByDate).map(([date, daySlots]) => (
                <div key={date}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                    {format(parseISO(date), 'EEEE, MMMM d')}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {daySlots.map((slot, i) => (
                      <button
                        key={i}
                        onClick={() => selectDoctorSlot(slot)}
                        className="card p-3 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-150 group"
                      >
                        <div className="flex items-center gap-2 text-xs font-semibold text-brand-700 group-hover:text-brand-800">
                          <Clock className="w-3.5 h-3.5" />
                          {slot.sessionType}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {slot.startTime} – {slot.endTime}
                        </div>
                        <div className="mt-2 flex items-center gap-1">
                          <div className={clsx(
                            'h-1 rounded-full flex-1 transition-all',
                            slot.remainingSlots > 5 ? 'bg-emerald-400' :
                            slot.remainingSlots > 2 ? 'bg-amber-400' : 'bg-red-400'
                          )} style={{ width: `${(slot.remainingSlots / slot.maxPatients) * 100}%` }} />
                          <div className="h-1 bg-slate-100 rounded-full flex-1" />
                          <span className={clsx(
                            'text-[10px] font-semibold ml-1',
                            slot.remainingSlots > 5 ? 'text-emerald-600' :
                            slot.remainingSlots > 2 ? 'text-amber-600' : 'text-red-600'
                          )}>
                            {slot.remainingSlots} left
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ MODE: SESSION ══════════════ */}

      {/* Session Mode — Step 1: Pick session + optional department */}
      {mode === 'session' && step === 1 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setMode(null)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Clock className="w-5 h-5 text-brand-600" />
              Choose a Session
            </h2>
          </div>

          <div className="card p-5 space-y-5">
            {/* Session toggle */}
            <div>
              <label className="label mb-2">Preferred Time</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setSessionFilter('Morning')}
                  className={clsx(
                    'flex-1 card p-4 text-center transition-all duration-200',
                    sessionFilter === 'Morning'
                      ? 'border-brand-500 bg-brand-50 shadow-sm'
                      : 'hover:border-brand-200'
                  )}
                >
                  <Sun className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                  <div className="font-semibold text-sm text-slate-900">Morning</div>
                  <div className="text-xs text-slate-500">Before 12 PM</div>
                </button>
                <button
                  onClick={() => setSessionFilter('Evening')}
                  className={clsx(
                    'flex-1 card p-4 text-center transition-all duration-200',
                    sessionFilter === 'Evening'
                      ? 'border-brand-500 bg-brand-50 shadow-sm'
                      : 'hover:border-brand-200'
                  )}
                >
                  <Moon className="w-6 h-6 text-indigo-500 mx-auto mb-1" />
                  <div className="font-semibold text-sm text-slate-900">Evening</div>
                  <div className="text-xs text-slate-500">After 12 PM</div>
                </button>
              </div>
            </div>

            {/* Optional department filter */}
            <div>
              <label className="label mb-2">Department (optional)</label>
              <select
                className="input"
                value={booking.departmentId ?? ''}
                onChange={(e) => setBooking((b) => ({ ...b, departmentId: e.target.value ? Number(e.target.value) : undefined }))}
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <button onClick={proceedSessionStep} className="btn btn-primary w-full justify-center btn-lg">
              Find Available Doctors <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Session Mode — Step 2: Browse doctors with slots */}
      {mode === 'session' && step === 2 && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              {sessionFilter} Slots
            </h2>
          </div>

          <div className="flex items-center gap-2 p-2.5 bg-brand-50 rounded-xl border border-brand-100 mb-5 text-xs text-brand-700 font-medium">
            {sessionFilter === 'Morning' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {sessionFilter} session
            {booking.departmentId && selectedDepartment && ` · ${selectedDepartment.name}`}
          </div>

          {/* Date range filter */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-600 whitespace-nowrap">Show slots up to:</span>
            <input
              type="date"
              className="input py-1 text-xs flex-1 max-w-[160px]"
              min={minToDate}
              value={slotToDate}
              onChange={(e) => setSlotToDate(e.target.value)}
            />
          </div>

          {sessionSlotsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : sessionSlots.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-500">No {sessionFilter.toLowerCase()} slots available in the next 7 days</p>
              <button onClick={() => setStep(1)} className="btn btn-secondary btn-sm mt-3">
                Change session
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Array.from(sessionSlotsByDoctor.values()).map((slot) => (
                <button
                  key={slot.doctorId}
                  onClick={() => selectFlexibleSlot(slot)}
                  className="card p-4 text-left hover:shadow-card-hover hover:border-brand-200 transition-all duration-150 group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex-shrink-0 relative overflow-hidden border border-slate-100">
                      {slot.doctorPhotoUrl && (
                        <img src={slot.doctorPhotoUrl} alt={slot.doctorName} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      )}
                      <div className="absolute inset-0 bg-brand-50 flex items-center justify-center">
                        <User className="w-5 h-5 text-brand-500" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors text-sm">
                          {slot.doctorTitle && `${slot.doctorTitle} `}{slot.doctorName}
                        </div>
                        {slot.isOnCall && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                            On-Call
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{slot.departmentName}</div>
                      {slot.doctorQualifications && (
                        <div className="text-xs text-slate-400 mt-0.5">{slot.doctorQualifications}</div>
                      )}
                      <div className="flex items-center gap-1.5 text-xs font-medium mt-2">
                        {slot.isOnCall ? (
                          <span className="text-amber-600 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            On Call — reception will confirm date &amp; time
                          </span>
                        ) : (
                          <span className="text-brand-700 flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" />
                            {format(parseISO(slot.slotDate), 'EEE, MMM d')}
                            <Clock className="w-3 h-3 ml-1" />
                            {slot.startTime} – {slot.endTime}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-1">
                        <div className={clsx(
                          'h-1 rounded-full flex-1 transition-all',
                          slot.remainingSlots > 5 ? 'bg-emerald-400' :
                          slot.remainingSlots > 2 ? 'bg-amber-400' : 'bg-red-400'
                        )} style={{ width: `${(slot.remainingSlots / slot.maxPatients) * 100}%` }} />
                        <div className="h-1 bg-slate-100 rounded-full flex-1" />
                        <span className={clsx(
                          'text-[10px] font-semibold ml-1',
                          slot.remainingSlots > 5 ? 'text-emerald-600' :
                          slot.remainingSlots > 2 ? 'text-amber-600' : 'text-red-600'
                        )}>
                          {slot.remainingSlots} left
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ SHARED: PATIENT DETAILS ══════════════ */}
      {mode !== null && step === detailsStep && (
        <div className="animate-slide-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setStep(step - 1)} className="btn btn-ghost btn-sm">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-brand-600" />
              Your Details
            </h2>
          </div>

          {/* Summary bar */}
          {(booking.slotDate || booking.isOnCall) && (
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-200 mb-5 text-xs text-slate-600 flex-wrap">
              <span className="flex items-center gap-1">
                <Stethoscope className="w-3.5 h-3.5 text-brand-500" />
                {selectedDoctor?.name}
              </span>
              {booking.slotDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-brand-500" />
                  {format(parseISO(booking.slotDate), 'EEE, MMM d')}
                </span>
              )}
              {booking.sessionType && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-brand-500" />
                  {booking.sessionType} session
                </span>
              )}
              {booking.isOnCall && (
                <span className="flex items-center gap-1 text-amber-600 font-medium">
                  On-Call — date to be confirmed by reception
                </span>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Full Name *</label>
                <input
                  className="input"
                  value={booking.patientName}
                  onChange={(e) => setBooking((b) => ({ ...b, patientName: e.target.value }))}
                  placeholder="Your full name"
                  required
                />
              </div>
              <div>
                <label className="label">Mobile Number *</label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    className="input pl-9"
                    type="tel"
                    value={booking.patientPhone}
                    onChange={(e) => setBooking((b) => ({ ...b, patientPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    placeholder="10-digit mobile"
                    pattern="[6-9][0-9]{9}"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="label">Gender</label>
                <select
                  className="input"
                  value={booking.patientGender}
                  onChange={(e) => setBooking((b) => ({ ...b, patientGender: e.target.value }))}
                >
                  <option value="">Prefer not to say</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Email (optional)</label>
                <input
                  className="input"
                  type="email"
                  value={booking.patientEmail}
                  onChange={(e) => setBooking((b) => ({ ...b, patientEmail: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div>
              <label className="label">Notes / Reason for visit</label>
              <textarea
                className="input resize-none h-20"
                value={booking.patientNotes}
                onChange={(e) => setBooking((b) => ({ ...b, patientNotes: e.target.value }))}
                placeholder="Briefly describe your symptoms or reason for visit..."
                maxLength={500}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setStep(step - 1)} className="btn btn-secondary">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary flex-1 justify-center btn-lg"
              >
                {submitting ? 'Booking...' : (
                  <>Confirm Booking <ChevronRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════ SHARED: CONFIRMATION ══════════════ */}
      {mode !== null && step === confirmationStep && confirmedAppointment && (
        <div className="animate-fade-up text-center max-w-md mx-auto">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-10 h-10 text-emerald-600" strokeWidth={1.5} />
          </div>

          <h2 className="text-2xl font-semibold text-slate-900 mb-2">
            {confirmedAppointment.isOnCallBooking ? 'Booking Request Received!' : 'Appointment Requested!'}
          </h2>
          <p className="text-sm text-slate-500 mb-6 leading-relaxed">
            {confirmedAppointment.isOnCallBooking
              ? `Our reception team will contact you at your phone number within 2 hours to confirm your appointment date and time with ${confirmedAppointment.doctorName}.`
              : 'Your appointment request has been submitted. The hospital will review and confirm it shortly.'}
          </p>

          <div className="card p-6 text-left mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="label">Appointment #</div>
                <div className="font-mono font-semibold text-brand-700 flex items-center gap-1.5">
                  {confirmedAppointment.appointmentNumber}
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(confirmedAppointment.appointmentNumber);
                      toast.success('Copied!');
                    }}
                    className="text-slate-400 hover:text-brand-600 transition-colors"
                    title="Copy appointment number"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div>
                <div className="label">Status</div>
                <span className={`status-${confirmedAppointment.status}`}>
                  {confirmedAppointment.status}
                </span>
              </div>
              <div>
                <div className="label">Doctor</div>
                <div className="font-medium text-slate-800">{confirmedAppointment.doctorName}</div>
              </div>
              <div>
                <div className="label">Date</div>
                <div className="font-medium text-slate-800">
                  {confirmedAppointment.appointmentDate
                    ? format(parseISO(confirmedAppointment.appointmentDate), 'EEE, MMM d yyyy')
                    : <span className="text-amber-600">To be confirmed</span>}
                </div>
              </div>
              <div>
                <div className="label">Session</div>
                <div className="font-medium text-slate-800">
                  {confirmedAppointment.sessionType ?? <span className="text-amber-600">TBC</span>}
                </div>
              </div>
              <div>
                <div className="label">Department</div>
                <div className="font-medium text-slate-800">{confirmedAppointment.departmentName}</div>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 mb-5">
            Please save your appointment number for future reference.
          </p>

          <button onClick={resetWizard} className="btn btn-primary justify-center w-full">
            Book Another Appointment
          </button>
        </div>
      )}
    </div>
  );
}
